import { ChangeEvent, FC, useState } from 'react';
import {
    Box,
    TextField,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    InputAdornment,
    Typography,
    Fade,
    CircularProgress,
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import LockIcon from '@mui/icons-material/Lock';
import SendIcon from '@mui/icons-material/Send';
import VerifiedIcon from '@mui/icons-material/Verified';
import { CountryPhoneCode } from '../../shared/util/phoneUtil';

interface LoginFormProps {
    onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
    inputProps: {
        value: any;
        onChange: (e: ChangeEvent<HTMLInputElement>) => void;
    };
    verificationCodeVisible: boolean;
    countryPhoneCodes: CountryPhoneCode[];
    selectedPrefix: string;
    onPrefixChange: (value: string) => void;
}

const LoginForm: FC<LoginFormProps> = ({
    onSubmit,
    inputProps,
    verificationCodeVisible,
    countryPhoneCodes,
    selectedPrefix,
    onPrefixChange,
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsSubmitting(true);
        try {
            await onSubmit(event);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handlePrefixChange = (event: any) => {
        onPrefixChange(event.target.value);
    };

    return (
        <Box component="form" onSubmit={handleSubmit} noValidate>
            <Fade in={true} timeout={1000}>
                <Box>
                    {/* Title */}
                    <Typography
                        variant="h5"
                        component="h1"
                        gutterBottom
                        sx={{
                            fontWeight: 600,
                            textAlign: 'center',
                            mb: 3,
                            color: 'text.primary',
                        }}
                    >
                        {verificationCodeVisible ? 'Verify Your Code' : 'Welcome Back'}
                    </Typography>

                    <Typography
                        variant="body2"
                        sx={{
                            textAlign: 'center',
                            mb: 4,
                            color: 'text.secondary',
                        }}
                    >
                        {verificationCodeVisible
                            ? 'Enter the verification code sent to your phone'
                            : 'Enter your phone number to sign in'}
                    </Typography>

                    {verificationCodeVisible ? (
                        <TextField
                            fullWidth
                            {...inputProps}
                            placeholder="Enter Verification Code"
                            variant="outlined"
                            autoFocus
                            sx={{ mb: 3 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LockIcon color="primary" />
                                    </InputAdornment>
                                ),
                                sx: {
                                    borderRadius: 2,
                                    fontSize: '1.1rem',
                                    letterSpacing: '0.3em',
                                    textAlign: 'center',
                                    '& input': {
                                        textAlign: 'center',
                                    },
                                },
                            }}
                            inputProps={{
                                maxLength: 6,
                                pattern: '[0-9]*',
                                inputMode: 'numeric',
                            }}
                        />
                    ) : (
                        <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                            <FormControl sx={{ minWidth: 120 }}>
                                <InputLabel>Code</InputLabel>
                                <Select
                                    value={selectedPrefix}
                                    onChange={handlePrefixChange}
                                    label="Code"
                                    sx={{ borderRadius: 2 }}
                                >
                                    {countryPhoneCodes.map((code) => (
                                        <MenuItem key={code.value} value={code.value}>
                                            {code.flag} {code.value}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                fullWidth
                                {...inputProps}
                                placeholder="Enter Phone Number"
                                variant="outlined"
                                autoFocus
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <PhoneIcon color="primary" />
                                        </InputAdornment>
                                    ),
                                    sx: {
                                        borderRadius: 2,
                                    },
                                }}
                                inputProps={{
                                    pattern: '[0-9]*',
                                    inputMode: 'numeric',
                                }}
                            />
                        </Box>
                    )}

                    {/* Submit Button */}
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        size="large"
                        disabled={isSubmitting}
                        endIcon={
                            isSubmitting ? (
                                <CircularProgress size={20} color="inherit" />
                            ) : verificationCodeVisible ? (
                                <VerifiedIcon />
                            ) : (
                                <SendIcon />
                            )
                        }
                        sx={{
                            borderRadius: 2,
                            py: 1.5,
                            fontSize: '1rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            boxShadow: 3,
                            '&:hover': {
                                boxShadow: 6,
                                transform: 'translateY(-2px)',
                            },
                            transition: 'all 0.3s ease',
                        }}
                    >
                        {isSubmitting
                            ? 'Processing...'
                            : verificationCodeVisible
                            ? 'Verify Code'
                            : 'Send Code'}
                    </Button>

                    {/* Help Text */}
                    {verificationCodeVisible && (
                        <Typography
                            variant="caption"
                            sx={{
                                display: 'block',
                                textAlign: 'center',
                                mt: 2,
                                color: 'text.secondary',
                            }}
                        >
                            Didn't receive the code? Check your phone or try again.
                        </Typography>
                    )}
                </Box>
            </Fade>
        </Box>
    );
};

export default LoginForm;